import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface FaqOptimizationRow {
  q_key: string;
  resolved: boolean;
  note: string | null;
  owner: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  updated_at: string | null;
}

export interface FaqOptimizationPatch {
  resolved?: boolean;
  note?: string | null;
  owner?: string | null;
  resolved_by?: string | null;
}

interface UseFaqOptimizationResult {
  rows: Record<string, FaqOptimizationRow>;
  loading: boolean;
  savingKey: string | null;
  saveOptimization: (qKey: string, patch: FaqOptimizationPatch) => Promise<void>;
  reload: () => void;
}

function emptyRow(qKey: string, now: string): FaqOptimizationRow {
  return {
    q_key: qKey,
    resolved: false,
    note: null,
    owner: null,
    resolved_by: null,
    resolved_at: null,
    updated_at: now,
  };
}

/**
 * 维护内容优化台账：每条 FAQ 的「已完善」状态、优化备注、负责人与操作人。
 * 采用乐观更新，写入失败时自动回滚。
 */
export function useFaqOptimization(): UseFaqOptimizationResult {
  const [rows, setRows] = useState<Record<string, FaqOptimizationRow>>({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const rowsRef = useRef(rows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("faq_optimization")
        .select("q_key, resolved, note, owner, resolved_by, resolved_at, updated_at")
        .limit(2000);
      if (error) throw error;
      const map: Record<string, FaqOptimizationRow> = {};
      (data ?? []).forEach((item) => {
        const record = item as FaqOptimizationRow;
        map[record.q_key] = record;
      });
      setRows(map);
    } catch {
      /* backend unavailable, keep empty set */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saveOptimization = useCallback(async (qKey: string, patch: FaqOptimizationPatch) => {
    setSavingKey(qKey);
    const now = new Date().toISOString();
    const previous = rowsRef.current[qKey];
    const base = previous ?? emptyRow(qKey, now);
    const next: FaqOptimizationRow = {
      ...base,
      ...patch,
      resolved_at:
        patch.resolved === true ? base.resolved_at ?? now : patch.resolved === false ? null : base.resolved_at,
      updated_at: now,
    };
    setRows((prev) => ({ ...prev, [qKey]: next }));
    try {
      const { error } = await supabase.from("faq_optimization").upsert(
        {
          q_key: next.q_key,
          resolved: next.resolved,
          note: next.note,
          owner: next.owner,
          resolved_by: next.resolved_by,
          resolved_at: next.resolved_at ?? now,
          updated_at: now,
        },
        { onConflict: "q_key" },
      );
      if (error) throw error;
    } catch {
      setRows((prev) => {
        const rollback = { ...prev };
        if (previous) rollback[qKey] = previous;
        else delete rollback[qKey];
        return rollback;
      });
    } finally {
      setSavingKey(null);
    }
  }, []);

  return { rows, loading, savingKey, saveOptimization, reload: load };
}